import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";

interface EnrollmentRow {
  enrollmentId: number;
  enquiryId: number;
  statusId?: number | null;
  statusName?: string | null;
  batchId?: number | null;
  batchName?: string | null;
  courseIds?: string | null;
  courseName?: string | null;
  schoolId?: number | null;
  yearId?: number | null;
  studentName?: string | null;
}

// Terminal enrollment statuses that must NOT be offered for a batch transfer
// (StudentEnrollment.Status: DROPOUT=3, PASSOUT=5).
const TERMINAL_STATUS_IDS = new Set([3, 5]);

interface BulkTransferModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "transferred" | "skipped" | "not_found" | "error";
  previousBatchId?: number | null;
  newBatchId?: number;
  reason?: string;
  error?: string;
}

const headerCellStyle: React.CSSProperties = {
  background: "var(--surface-page)",
  color: "var(--text-body)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-default)",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const bodyCellStyle: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "0.8rem",
  color: "var(--text-strong)",
  borderBottom: "1px solid var(--border-subtle)",
};

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    transferred: { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:     { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found:   { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:       { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkTransferModal: React.FC<BulkTransferModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const [batches, setBatches] = useState<
    Array<{
      id: number;
      name: string;
      schoolId?: number | null;
      yearId?: number | null;
      batchTypeId?: number | null;
    }>
  >([]);
  const [batchCourses, setBatchCourses] = useState<
    Array<{
      batchId: number;
      courseId: number | null;
      programId: number | null;
      deliveryFormatId: number | null;
    }>
  >([]);
  const [isProgramBased, setIsProgramBased] = useState(false);
  const [schools, setSchools] = useState<Array<{ id: number; name: string }>>([]);
  const [years, setYears] = useState<Array<{ id: number; name: string }>>([]);
  const [currentYearId, setCurrentYearId] = useState<number | null>(null);
  const [deliveryFormats, setDeliveryFormats] = useState<Array<{ id: number; name: string }>>([]);
  const [courseDeliveryFormats, setCourseDeliveryFormats] = useState<
    Array<{ courseId: number | null; deliveryFormatId: number | null }>
  >([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [centreId, setCentreId] = useState<number | "">("");
  const [yearId, setYearId] = useState<number | "">("");
  const [deliveryFormatId, setDeliveryFormatId] = useState<number | "">("");
  const [batchType, setBatchType] = useState<1 | 2>(1);
  const [targetBatchId, setTargetBatchId] = useState<number | "">("");
  const [subBatchId, setSubBatchId] = useState<number | "">("");
  const [transferDate, setTransferDate] = useState<string>("");
  const [reasonForTransfer, setReasonForTransfer] = useState("");
  const [remarks, setRemarks] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  // Which enrollments to actually transfer. A student can have several enrollments;
  // we must NOT move all of them by default (that was the bug). See the seeding effect.
  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState<Set<number>>(
    new Set(),
  );

  const enrollments: EnrollmentRow[] = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);

    // Skip terminal (passout/dropout) enrollments — they can't be transferred.
    const out: EnrollmentRow[] = [];
    for (const eqId of selectedEnquiryIds) {
      const list = enquiryEnrollmentsMap[eqId] || [];
      for (const enr of list) {
        if (TERMINAL_STATUS_IDS.has(enr.statusId ?? -1)) continue;
        out.push({
          enrollmentId: enr.enrollmentId,
          enquiryId: eqId,
          statusId: enr.statusId,
          statusName: enr.statusName,
          batchId: enr.batchId,
          batchName: enr.batchName,
          courseIds: enr.courseIds,
          courseName: enr.courseName,
          schoolId: enr.schoolId,
          yearId: enr.yearId,
          studentName: nameByEnquiryId.get(eqId) || "",
        });
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  // Seed the default selection to ONE enrollment per enquiry — the first row per
  // enquiry, which arrives ordered by enrollmentId DESC (most recent). This prevents
  // a two-enrollment student from transferring both by default; the user can toggle
  // any row. Re-seed only when the enrollment set changes (not after a submit/result).
  const seededKeyRef = useRef<string>("");
  useEffect(() => {
    if (results) return;
    const key = enrollments.map((e) => e.enrollmentId).join(",");
    if (key === seededKeyRef.current) return;
    seededKeyRef.current = key;
    const seenEnquiry = new Set<number>();
    const next = new Set<number>();
    for (const e of enrollments) {
      if (seenEnquiry.has(e.enquiryId)) continue;
      seenEnquiry.add(e.enquiryId);
      next.add(e.enrollmentId);
    }
    setSelectedEnrollmentIds(next);
  }, [enrollments, results]);

  const toggleEnrollment = (enrollmentId: number) => {
    setSelectedEnrollmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(enrollmentId)) next.delete(enrollmentId);
      else next.add(enrollmentId);
      return next;
    });
  };

  // Derive, from the ticked enrollments' course(s): the "New Batch" list, the
  // "Select New Sub Batch" list, and the Delivery Format options — all scoped to the
  // student's course (legacy getBatchesForCourseOrProgramBasedSystem), narrowed by
  // Centre/Year, with the current batch removed. New Batch = regular batches
  // (BatchTypeId != 2); Sub Batch = BatchTypeId == 2. Delivery format narrows only
  // the regular batches (legacy applies it on the BATCH cascade only).
  const derived = useMemo(() => {
    const empty = { batches: [] as typeof batches, subBatches: [] as typeof batches, formats: [] as typeof deliveryFormats };
    if (selectedEnrollmentIds.size === 0) return empty;

    const currentBatchIds = new Set<number>();
    const allowedKeys = new Set<number>();
    for (const e of enrollments) {
      if (!selectedEnrollmentIds.has(e.enrollmentId)) continue;
      if (e.batchId != null) currentBatchIds.add(e.batchId);
      for (const raw of (e.courseIds || "").split(",")) {
        const n = Number(raw.trim());
        if (Number.isInteger(n) && n > 0) allowedKeys.add(n);
      }
    }
    // Course unresolved → show nothing rather than every batch (the original bug).
    if (allowedKeys.size === 0) return empty;

    // Batch ids mapped to the student's course/program + the delivery-format ids
    // present on those course batches (for the Delivery Format dropdown).
    const okBatchIds = new Set<number>();
    const formatIdsForCourse = new Set<number>();
    for (const bc of batchCourses) {
      const key = isProgramBased ? bc.programId : bc.courseId;
      if (key != null && allowedKeys.has(key)) {
        okBatchIds.add(bc.batchId);
        if (bc.deliveryFormatId != null) formatIdsForCourse.add(bc.deliveryFormatId);
      }
    }
    // Batch ids carrying the SELECTED delivery format (narrows regular batches).
    const dfBatchIds = new Set<number>();
    if (deliveryFormatId) {
      for (const bc of batchCourses) {
        if (bc.deliveryFormatId === deliveryFormatId) dfBatchIds.add(bc.batchId);
      }
    }

    // Delivery Format options = the student's COURSE's mapped formats (legacy source:
    // course_delivery_format_course_mapping), not merely the formats present on
    // batches. Course-based only (allowedKeys = courseIds); program-based tenants
    // fall back to the batch-derived set.
    const courseFormatIds = new Set<number>();
    if (!isProgramBased) {
      for (const cdf of courseDeliveryFormats) {
        if (cdf.courseId != null && allowedKeys.has(cdf.courseId) && cdf.deliveryFormatId != null) {
          courseFormatIds.add(cdf.deliveryFormatId);
        }
      }
    }
    const formatIds = courseFormatIds.size > 0 ? courseFormatIds : formatIdsForCourse;

    const inScope = (b: (typeof batches)[number]) =>
      okBatchIds.has(b.id) &&
      !currentBatchIds.has(b.id) &&
      (!centreId || b.schoolId === centreId) &&
      (!yearId || b.yearId == null || b.yearId === yearId);

    return {
      batches: batches.filter(
        (b) => b.batchTypeId !== 2 && inScope(b) && (!deliveryFormatId || dfBatchIds.has(b.id)),
      ),
      subBatches: batches.filter((b) => b.batchTypeId === 2 && inScope(b)),
      formats: deliveryFormats.filter((f) => formatIds.has(f.id)),
    };
  }, [
    batches,
    batchCourses,
    isProgramBased,
    enrollments,
    selectedEnrollmentIds,
    centreId,
    yearId,
    deliveryFormatId,
    deliveryFormats,
    courseDeliveryFormats,
  ]);

  const availableBatches = derived.batches;
  const availableSubBatches = derived.subBatches;
  const deliveryFormatOptions = derived.formats;

  // Distinct course names of the ticked enrollments — shown read-only (legacy "Course").
  const selectedCourseLabel = useMemo(() => {
    const names = new Set<string>();
    for (const e of enrollments) {
      if (!selectedEnrollmentIds.has(e.enrollmentId)) continue;
      if (e.courseName) names.add(e.courseName);
    }
    return Array.from(names).join(", ");
  }, [enrollments, selectedEnrollmentIds]);

  // If the picked batch / sub-batch is no longer offered (user changed course,
  // centre, year or delivery format), clear the stale selection.
  useEffect(() => {
    if (targetBatchId === "") return;
    if (!availableBatches.some((b) => b.id === targetBatchId)) {
      setTargetBatchId("");
    }
  }, [availableBatches, targetBatchId]);

  useEffect(() => {
    if (subBatchId === "") return;
    if (!availableSubBatches.some((b) => b.id === subBatchId)) {
      setSubBatchId("");
    }
  }, [availableSubBatches, subBatchId]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data?.data || {};
        setBatches(d.batches || []);
        setBatchCourses(d.batchCourses || []);
        setIsProgramBased(!!d.isProgramBased);
        setSchools(d.schools || []);
        setYears(d.years || []);
        setCurrentYearId(d.currentYearId ?? null);
        setDeliveryFormats(d.deliveryFormats || []);
        setCourseDeliveryFormats(d.courseDeliveryFormats || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  // Seed defaults ONCE per open, after lookups load (so manual edits stick):
  // Transfer Date = today; Centre = the ticked enrollments' common school; Year = the
  // current academic year (legacy getYearCurrent → year_master.CurrentYear=1).
  const defaultsSeededRef = useRef(false);
  useEffect(() => {
    if (!show) {
      defaultsSeededRef.current = false;
      return;
    }
    if (
      defaultsSeededRef.current ||
      loadingLookups ||
      enrollments.length === 0 ||
      selectedEnrollmentIds.size === 0
    ) {
      return;
    }
    defaultsSeededRef.current = true;
    setTransferDate(new Date().toISOString().slice(0, 10));
    // Centre = common school of the TICKED enrollments only (else "(all centres)").
    let school: number | "" = "";
    for (const e of enrollments) {
      if (!selectedEnrollmentIds.has(e.enrollmentId)) continue;
      const v = e.schoolId;
      if (v == null) {
        school = "";
        break;
      }
      if (school === "") school = v;
      else if (school !== v) {
        school = "";
        break;
      }
    }
    setCentreId(school);
    setYearId(currentYearId ?? "");
  }, [show, loadingLookups, enrollments, selectedEnrollmentIds, currentYearId]);

  const handleClose = () => {
    setCentreId("");
    setYearId("");
    setDeliveryFormatId("");
    setBatchType(1);
    setTargetBatchId("");
    setSubBatchId("");
    setTransferDate("");
    setReasonForTransfer("");
    setRemarks("");
    setResults(null);
    setSummary(null);
    setError("");
    setSelectedEnrollmentIds(new Set());
    seededKeyRef.current = "";
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (batchType === 1 && !targetBatchId) {
      setError("Pick a new batch.");
      return;
    }
    if (batchType === 2 && !subBatchId) {
      setError("Pick a new sub batch.");
      return;
    }
    if (enrollments.length === 0) {
      setError("The selected students have no enrollments.");
      return;
    }
    const idsToTransfer = enrollments
      .map((e) => e.enrollmentId)
      .filter((id) => selectedEnrollmentIds.has(id));
    if (idsToTransfer.length === 0) {
      setError("Select at least one enrollment to transfer.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkTransfer(
        batchType === 2
          ? {
              enrollmentIds: idsToTransfer,
              batchType: 2,
              subBatchId: Number(subBatchId),
              transferDate: transferDate || null,
              reasonForTransfer: reasonForTransfer.trim() || null,
              remarks: remarks.trim() || null,
            }
          : {
              enrollmentIds: idsToTransfer,
              batchType: 1,
              targetBatchId: Number(targetBatchId),
              targetYearId: yearId ? Number(yearId) : null,
              deliveryFormatId: deliveryFormatId ? Number(deliveryFormatId) : null,
              transferDate: transferDate || null,
              reasonForTransfer: reasonForTransfer.trim() || null,
              remarks: remarks.trim() || null,
            },
      );
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results
            ? "Bulk Student Batch Transfer — Results"
            : "Bulk Student Batch Transfer"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)", marginBottom: 6 }}>
                  Enrollments — tick the ones to move ({selectedEnrollmentIds.size} of{" "}
                  {enrollments.length} selected)
                </div>

                {enrollments.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      background: "#fefce8",
                      color: "#854d0e",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    The selected students have no enrollments. Close this dialog and pick rows that
                    have at least one enrollment.
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: 240,
                      overflow: "auto",
                      border: "1px solid var(--border-default)",
                      borderRadius: 6,
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ ...headerCellStyle, width: 36, textAlign: "center" }} />
                          <th style={headerCellStyle}>Enrollment</th>
                          <th style={headerCellStyle}>Student</th>
                          <th style={headerCellStyle}>Course</th>
                          <th style={headerCellStyle}>Status</th>
                          <th style={headerCellStyle}>Current Batch</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrollments.map((e) => (
                          <tr key={e.enrollmentId}>
                            <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={selectedEnrollmentIds.has(e.enrollmentId)}
                                onChange={() => toggleEnrollment(e.enrollmentId)}
                                style={{ margin: 0, cursor: "pointer" }}
                              />
                            </td>
                            <td style={bodyCellStyle}>
                              <span style={{ fontWeight: 600 }}>#{e.enrollmentId}</span>
                            </td>
                            <td style={bodyCellStyle}>
                              <span style={{ color: "var(--text-strong)" }}>{e.studentName}</span>
                              <span style={{ marginLeft: 6, color: "var(--text-faint)", fontSize: 11 }}>
                                Enq {e.enquiryId}
                              </span>
                            </td>
                            <td style={bodyCellStyle}>
                              <span style={{ color: "var(--text-body)" }}>{e.courseName || "—"}</span>
                            </td>
                            <td style={bodyCellStyle}>
                              <span style={{ color: "var(--text-body)" }}>{e.statusName || "—"}</span>
                            </td>
                            <td style={bodyCellStyle}>
                              <span style={{ color: "var(--text-body)" }}>{e.batchName || "—"}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
                  <Form.Group className="mb-3" style={{ flex: "1 1 100%" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Course</Form.Label>
                    <div style={{ fontSize: 13, color: "var(--text-body)", padding: "6px 0" }}>
                      {selectedCourseLabel || "—"}
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-3" style={{ flex: "1 1 240px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Centre</Form.Label>
                    <Form.Select
                      className="form-control"
                      size="sm"
                      value={centreId}
                      onChange={(e) => setCentreId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">(all centres)</option>
                      {schools.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3" style={{ flex: "1 1 240px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Year</Form.Label>
                    <Form.Select
                      className="form-control"
                      size="sm"
                      value={yearId}
                      onChange={(e) => setYearId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">(all years)</option>
                      {years.map((y) => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <Form.Group className="mb-3" style={{ maxWidth: 380 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Delivery Format</Form.Label>
                  <Form.Select
                    className="form-control"
                    size="sm"
                    value={deliveryFormatId}
                    onChange={(e) => setDeliveryFormatId(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">#None#</option>
                    {deliveryFormatOptions.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Batch Type</Form.Label>
                  <div style={{ display: "flex", gap: 20 }}>
                    <Form.Check
                      type="radio"
                      id="bt-batch"
                      name="batchType"
                      label="Batch"
                      checked={batchType === 1}
                      onChange={() => setBatchType(1)}
                      style={{ fontSize: 13 }}
                    />
                    <Form.Check
                      type="radio"
                      id="bt-subbatch"
                      name="batchType"
                      label="Sub Batch"
                      checked={batchType === 2}
                      onChange={() => setBatchType(2)}
                      style={{ fontSize: 13 }}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3" style={{ maxWidth: 380 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Select New Sub Batch{" "}
                    {batchType === 2 && <span style={{ color: "var(--status-danger)" }}>*</span>}
                  </Form.Label>
                  <Form.Select
                    className="form-control"
                    size="sm"
                    value={subBatchId}
                    onChange={(e) => setSubBatchId(e.target.value ? Number(e.target.value) : "")}
                    disabled={batchType !== 2}
                  >
                    <option value="">
                      {batchType !== 2
                        ? "#None#"
                        : selectedEnrollmentIds.size === 0
                        ? "Tick an enrollment above first…"
                        : availableSubBatches.length === 0
                        ? "No sub batches for the selected student's course"
                        : "Select sub batch…"}
                    </option>
                    {availableSubBatches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" style={{ maxWidth: 380 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Select New Batch{" "}
                    {batchType === 1 && <span style={{ color: "var(--status-danger)" }}>*</span>}
                  </Form.Label>
                  <Form.Select
                    className="form-control"
                    size="sm"
                    value={targetBatchId}
                    onChange={(e) => setTargetBatchId(e.target.value ? Number(e.target.value) : "")}
                    disabled={batchType !== 1}
                  >
                    <option value="">
                      {batchType !== 1
                        ? "#None#"
                        : selectedEnrollmentIds.size === 0
                        ? "Tick an enrollment above first…"
                        : availableBatches.length === 0
                        ? "No batches found for the selected student's course"
                        : "Select batch…"}
                    </option>
                    {availableBatches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Form.Select>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
                    Only batches in the selected student's course are shown.
                  </div>
                </Form.Group>

                <Form.Group className="mb-3" style={{ maxWidth: 240 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Transfer Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                  />
                </Form.Group>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: "1 1 240px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Reason for transfer
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={reasonForTransfer}
                      onChange={(e) => setReasonForTransfer(e.target.value)}
                      placeholder="Optional"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3" style={{ flex: "1 1 240px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Remarks</Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Optional"
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.transferred ?? 0}</strong> transferred
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "transferred" && (
                          <span style={{ color: "var(--text-body)" }}>
                            batch {r.previousBatchId ?? "—"} → {r.newBatchId}
                          </span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_in_batch"
                              ? "Already in this batch"
                              : r.reason === "already_in_sub_batch"
                              ? "Already in this sub batch"
                              : r.reason === "not_in_sub_batch"
                              ? "Not in any sub batch"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                loadingLookups ||
                (batchType === 1 ? !targetBatchId : !subBatchId) ||
                selectedEnrollmentIds.size === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Transferring…
                </>
              ) : (
                <>Transfer ({selectedEnrollmentIds.size})</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkTransferModal;
